import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
import {
  faBed,
  faBus,
  faCar,
  faClock,
  faExchangeAlt,
  faExternalLinkAlt,
  faFlask,
  faHand,
  faHotel,
  faPaperPlane,
  faPlus,
  faRefresh,
  faSpinner,
  faWarning,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAssignmentRequestsGetAssignmentRequestsForUserAndDate } from 'api/assignment-requests/assignment-requests';
import {
  useAssignmentGetBookedAssignmentsForUserAndDate,
  useAssignmentSendPlannedAssignmentsForUser,
} from 'api/assignment/assignment';
import {
  AssigneeWarning,
  AssignmentRequestModel,
  AssignmentStatusEnum,
  AssignmentTypeEnum,
  AssignmentViewModel,
  AvailabilityDto,
  CaseTypeEnum,
  DayIncompleteReasonEnum,
  GlobalSettingEnum,
  UserModel,
} from 'api/model';
import Routes from 'constants/Routes';
import useCompanies from 'contexts/basicData/useCompanies';
import useTranslations from 'contexts/basicData/useTranslations';
import useModalStack from 'contexts/modal/useModalStack';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import {
  formatTime,
  getHoursAndMinutesFromMillisecondsString,
} from 'utils/date-helpers';
import Address from './Address';
import TextButton from 'components/inputs/TextButton';
import PrioritizedDayTransferUserPicker from 'components/PrioritizedDayTransferUserPicker';
import type { ExtendedDayTransferUserModel } from 'components/PrioritizedDayTransferUserPicker';
import useZipCodeAreas from 'hooks/useZipCodeAreas';
import useTransferAssignments from './useTransferAssignments';
import getPlaceholderIcon, { getAssignmentTypeIcon } from './worktime-helper'; // getLastAssignmentDetails,
import useCreatePlaceholderAssignments, {
  PlaceholderAssignmentTypeEnum,
} from './useCreatePlaceholderAssignments';
import useDayDrivingTimes, { DayDrivingTime } from './useDayDrivingTimes';
import useCreateAssignmentRequests from './CreateAssignmentRequest/useCreateAssignmentRequests';
import DropdownMenu from 'components/DropdownMenu';
import useFindSuitableAssignments from './useFindSuitableAssignments';
import { useQueryClient } from '@tanstack/react-query';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import { useUserGetDrivingPolicyReportForUnPlannedAssignment } from 'api/user/user';
import useIsInLogisticVicinity from './useIsInLogisticVicinity';
import AssignmentRequestModalWrapper from 'pages/InternalDeliveryGroup/Assignments/components/AssignmentRequestModalWrapper';
import useGlobalSettings from 'hooks/useGlobalSettings';
import useZipCodeLocalAreas from 'hooks/useZipCodeLocalAreas';
import DetailedCaseProvider from 'components/DetailedCase/DetailedCaseProvider';
import DetailedCaseFormProvider from 'components/DetailedCase/DetailedCaseFormProvider';
import { CaseModal } from 'pages/Case';
import {} from 'components/AssignmentsList/AssignmentListStyles';
import AdjustAssignmentTimesButton from './AdjustAssignmentTimesButton';
import TrimBreakTimeButton from './TrimBreakTimeButton';
import PlaceHolderCaseActionsButton from './PlaceholderCaseActionButton';
import ConfirmModal from 'components/Modal/ConfirmModal';
import { useRecoilState } from 'recoil';
import hoveredDayAssignmentIdAtom from './hoveredAssignmentAtom';

const MIN_TIME_BETWEEN_ASSIGNMENTS_MILLIS = 15 * 60 * 1000; // 15 minutes

const isCanceledEntry = (entry: DayDrivingTime) =>
  entry.assignment != null &&
  entry.assignment.assignment.assignmentCancelTypeID != null;

// Backend recently started returning day-level incomplete-planning reasons and
// the assignment IDs that contributed to each. Orval may not yet have regenerated
// these fields on GetBookedAssignmentsForUserAndDateList, so accept either shape.
type IncompletePlanFieldsCarrier =
  | {
      dayIncompleteReasons?: DayIncompleteReasonEnum[] | null;
      contributingAssignmentIdsByReason?:
        | Record<string, number[]>
        | null;
    }
  | null
  | undefined;
const extractIncompletePlanFields = (
  carrier: IncompletePlanFieldsCarrier
): {
  dayIncompleteReasons: DayIncompleteReasonEnum[];
  contributingAssignmentIdsByReason: Record<string, number[]>;
} => ({
  dayIncompleteReasons: carrier?.dayIncompleteReasons ?? [],
  contributingAssignmentIdsByReason:
    carrier?.contributingAssignmentIdsByReason ?? {},
});

const DAY_INCOMPLETE_REASON_LABELS: Record<DayIncompleteReasonEnum, string> = {
  [DayIncompleteReasonEnum.StartingOutsideCounty]:
    'Dagen börjar utanför hemlänet',
  [DayIncompleteReasonEnum.EndingOutsideCounty]: 'Dagen slutar utanför hemlänet',
  [DayIncompleteReasonEnum.MidDayDifferentCounty]:
    'Annat län mitt på dagen utan utfyllnad',
  [DayIncompleteReasonEnum.NotEnoughBreakTime]: 'Otillräcklig rasttid',
};

const Warn = styled.span`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.foreground.error};
  font-weight: bold;
  padding-bottom: 5px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  position: relative;
  gap: 0px;
`;

const Title = styled.h2`
  margin-top: 15px;
  font-size: ${({ theme }) => theme.sizes.font.medium};

  &:first-of-type {
    margin-top: 0;
  }
`;

const CaseButton = styled(TextButton)<{ strikeThrough?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
  gap: 5px;
  padding: 2px;
  width: 100%;
`;

const RowWrapper = styled.div`
  display: grid;
  grid-template-columns: 20px 240px 180px 60px 120px 1fr 1fr;
  align-items: center;
  padding: 5px;

  // Make each direct child a grid with three rows,
  // and apply truncation for overflow text
  & > * {
    display: grid;
    grid-template-rows: repeat(3, auto);
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    // Make the first element in each subgrid bold
    & > :first-child {
      font-weight: bold;
    }
  }

  /* highlight row on hover */
  &:hover {
    background-color: ${({ theme }) => theme.colors.background.secondary};
  }
`;

const RowActionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;
`;

const ExtraInfo = styled.div`
  grid-column: 1 / -1; /* span all columns of RowWrapper grid */
  padding: 8px 12px;
  margin-top: 6px;
  max-width: 100%;
  word-break: break-word;
  white-space: wrap;
  & p {
    margin: 0 0 4px 0;
  }
`;

const AddressWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 5px;

  // sub-div should have border
  div {
    border: 1px solid ${({ theme }) => theme.colors.border.primary};
    border-radius: 3px;
    padding: 5px;
  }
  // sub-div should have border error color if className is error
  div.error {
    border-color: ${({ theme }) => theme.colors.foreground.error};
  }
  div.warning {
    border-color: ${({ theme }) => theme.colors.foreground.warning};
  }
  margin-bottom: 5px;
`;

const Label = styled.span<{ level?: 'info' | 'warning' | 'error' }>`
  color: ${({ theme, level = 'info' }) => {
    switch (level) {
      case 'warning':
        return theme.colors.foreground.warning;
      case 'error':
        return theme.colors.foreground.error;
      default:
        return theme.colors.foreground.primary;
    }
  }};
  font-weight: bold;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  gap: 10px;
  user-select: none;
  align-items: center;
`;

const AssignmentRow: React.FC<{
  drivingTimes: DayDrivingTime;
  model: AssignmentViewModel;
  onUpdate?: () => void;
  incompleteReasons?: DayIncompleteReasonEnum[];
}> = ({ model, drivingTimes, onUpdate, incompleteReasons }) => {
  const modalStack = useModalStack();
  const { zipCodeLocalAreaFromZipCode } = useZipCodeLocalAreas();
  const { activeCompaniesByAddrDict, getCompanyAddressDictKey } =
    useCompanies();
  const {
    assignmentTypesShort,
    caseTypes,
    assignmentStatuses,
    cancelTypes,
    assignmentDeviations,
    stayOverTypes,
  } = useTranslations();

  const drivingPolicyReportResult =
    useUserGetDrivingPolicyReportForUnPlannedAssignment(
      {
        userId: model.assignment.assignedTo?.userID,
        bookedTo: model.assignment.bookedTo,
        startOrEstimatedStartTime:
          model.assignment.startTime ?? model.assignment.estimatedStartTime,
        estimatedDuration: model.assignment.estimatedDuration,
        endOrEstimatedEndTime: model.assignment.endTime,
        excludeAssignmentId: model.assignment.assignmentID,
      },
      {
        query: {
          enabled:
            model.assignment.assignmentTypeID ===
            AssignmentTypeEnum.CarPlaceholder,
        },
      }
    );

  const isCanceled = model.assignment.assignmentCancelTypeID != null;
  const hasIncompleteReasons =
    incompleteReasons != null && incompleteReasons.length > 0;
  const [hoveredId, setHoveredId] = useRecoilState(hoveredDayAssignmentIdAtom);
  const isHovered = hoveredId === model.assignment.assignmentID;

  const rowStyle: React.CSSProperties = {};
  if (hasIncompleteReasons) {
    rowStyle.boxShadow = 'inset 4px 0 0 0 #d33';
    rowStyle.background = 'rgba(221,51,51,0.06)';
  }
  if (isHovered) {
    rowStyle.outline = '2px solid #EA580C';
    rowStyle.outlineOffset = '-2px';
  }

  return (
    <RowWrapper
      style={rowStyle}
      onMouseEnter={() => setHoveredId(model.assignment.assignmentID)}
      onMouseLeave={() => setHoveredId(null)}
    >
      <div>
        <FontAwesomeIcon
          icon={
            model.case.caseTypeID === CaseTypeEnum.Placeholder
              ? getPlaceholderIcon(model.assignment.assignmentTypeID)
              : getAssignmentTypeIcon(model.assignment.assignmentTypeID)
          }
        />

        {isCanceled && (
          <Label level="warning">
            <FontAwesomeIcon
              icon={faWarning}
              title={
                assignmentDeviations[
                  model.assignment.assignmentDeviationID
                ]?.toUpperCase() ?? ''
              }
            />
          </Label>
        )}
      </div>

      <div>
        <p>
          {model.case.registrationNumber} ({caseTypes[model.case.caseTypeID]},{' '}
          {assignmentTypesShort[model.assignment.assignmentTypeID]})
        </p>
        <p>Start: {formatTime(drivingTimes.start)}</p>
        <p>
          Slut: {formatTime(drivingTimes.end)}{' '}
          {drivingTimes.breakTimeMinutes > 0
            ? `Rast: ${drivingTimes.breakTimeMinutes} min`
            : ''}
        </p>
        {model.assignment.fixedAssignmentTime != null &&
          model.assignment.fixedAssignmentTime > 0 && (
            <p style={{ fontSize: '0.85em', color: '#555' }}>
              Fast: {getHoursAndMinutesFromMillisecondsString(
                model.assignment.fixedAssignmentTime
              )}
              {model.assignment.breakTime > 0 && (
                <>
                  {' '}/ Rast: {model.assignment.breakTime} min /{' '}
                  <strong>
                    Totalt:{' '}
                    {getHoursAndMinutesFromMillisecondsString(
                      model.assignment.fixedAssignmentTime +
                        model.assignment.breakTime * 60 * 1000
                    )}
                  </strong>
                </>
              )}
            </p>
          )}
        {model.assignment.stayOverTypeID !== null && (
          <Label level="warning">
            <FontAwesomeIcon icon={faBed} title="Övernattning" />{' '}
            {stayOverTypes[model.assignment.stayOverTypeID!]}{' '}
          </Label>
        )}
        {hasIncompleteReasons && (
          <Label level="error" title="Bidrar till ofullständig dagsplanering">
            <FontAwesomeIcon icon={faWarning} />{' '}
            {incompleteReasons!
              .map((r) => DAY_INCOMPLETE_REASON_LABELS[r] ?? r)
              .join('; ')}
          </Label>
        )}
        {drivingTimes.hasEnoughBreakTimeUntilAssignmentEnd === false && (
          <Label level="error">
            <FontAwesomeIcon
              icon={faClock}
              style={{ color: 'red', marginLeft: 'auto' }}
              title="Otillräcklig rasttid"
            />
            Otillräcklig rasttid
            <AdjustAssignmentTimesButton
              assignment={model.assignment}
              caseModel={model.case}
              onUpdate={onUpdate}
            />
          </Label>
        )}
        {drivingTimes.hasTooMuchBreakTimeUntilAssignmentEnd === true && (
          <>
            <Label level="info" style={{ color: '#1976d2' }}>
              <FontAwesomeIcon
                icon={faClock}
                style={{ color: '#1976d2', marginLeft: 'auto' }}
                title="Planerad rast överstiger kravet (30 min per 5h) – inte ett regelbrott, bara en policy-avvikelse"
              />
              Mer rast än krävt
            </Label>
            {drivingTimes.minRequiredBreakMinutesForAssignment !== undefined &&
              model.assignment.breakTime >
                drivingTimes.minRequiredBreakMinutesForAssignment && (
                <TrimBreakTimeButton
                  assignment={model.assignment}
                  caseModel={model.case}
                  minBreakMinutes={
                    drivingTimes.minRequiredBreakMinutesForAssignment
                  }
                  onUpdate={onUpdate}
                />
              )}
          </>
        )}
      </div>

      <div>
        <p>Adress </p>
        <p>
          {activeCompaniesByAddrDict.get(
            getCompanyAddressDictKey(
              model.assignment.fromAddress,
              model.assignment.fromZip,
              model.assignment.fromCity
            )
          )?.name || model.assignment.fromAddress}
        </p>
        <p>
          {activeCompaniesByAddrDict.get(
            getCompanyAddressDictKey(
              model.assignment.toAddress,
              model.assignment.toZip,
              model.assignment.toCity
            )
          )?.name || model.assignment.toAddress}
        </p>
      </div>
      <div>
        <p>Postnr</p>
        <p>{model.assignment.fromZip}</p>
        <p>{model.assignment.toZip}</p>
      </div>
      <div>
        <p>Ort</p>
        <p>
          {zipCodeLocalAreaFromZipCode(model.assignment.fromZip)?.name ??
            model.assignment.fromCounty?.areaName}
        </p>
        <p>
          {zipCodeLocalAreaFromZipCode(model.assignment.toZip)?.name ??
            model.assignment.toCounty?.areaName}
        </p>
      </div>
      <div>
        <p>Status</p>
        <p>{assignmentStatuses[model.assignment.assignmentStatusID]}</p>

        {model.assignment.assignmentCancelTypeID ? (
          <p>{cancelTypes[model.assignment.assignmentCancelTypeID]}</p>
        ) : (
          <br />
        )}
      </div>
      <RowActionWrapper>
        {model.case.caseTypeID === CaseTypeEnum.Placeholder && (
          <PlaceHolderCaseActionsButton
            model={model}
            drivingPolicyReport={drivingPolicyReportResult.data}
          />
        )}
        <CaseButton
          onClick={(e) => {
            // Prevent the button from stealing focus if needed
            e.preventDefault();

            const url = `${Routes.cases.index}/${model.case.caseID}`;

            if (e.ctrlKey || e.metaKey) {
              // Ctrl (Windows/Linux) or Cmd (Mac) was pressed → open in new tab
              window.open(url, model.case.caseID.toString());
            } else {
              // Normal click → open in wrapper modal
              modalStack.push(
                <DetailedCaseProvider caseId={model.case.caseID}>
                  <DetailedCaseFormProvider>
                    <CaseModal
                      onClose={() => modalStack.pop()}
                      assignmentIdForMarkingAsHandled={
                        model.assignment.assignmentID
                      }
                    />
                  </DetailedCaseFormProvider>
                </DetailedCaseProvider>
              );
            }
          }}
          strikeThrough={isCanceled}
        >
          <FontAwesomeIcon
            style={{ marginLeft: 'auto' }}
            icon={faExternalLinkAlt}
          />
        </CaseButton>
      </RowActionWrapper>
      {model.assignment.extraInfo?.length > 0 && (
        <ExtraInfo>
          <p>Information från Ride:</p>
          {model.assignment.extraInfo}
        </ExtraInfo>
      )}
    </RowWrapper>
  );
};

const AssignmentRequestRow: React.FC<{
  drivingTimes: DayDrivingTime;
  model: AssignmentRequestModel;
}> = ({ model, drivingTimes }) => {
  const { assignmentRequestStatuses } = useTranslations();
  const { zipCodeLocalAreaFromZipCode } = useZipCodeLocalAreas();

  const modalStack = useModalStack();
  const { companiesById } = useCompanies();
  return (
    <RowWrapper>
      <span>
        <FontAwesomeIcon icon={faHand} title="Annonsförfrågan" />
      </span>
      <span>
        <p>Annonsförfrågan</p>
        <p>Start: {formatTime(drivingTimes.start)}</p>
        <p>Slut: {formatTime(drivingTimes.end)}</p>
      </span>
      <span>
        <p>Adress </p>
        <p>{companiesById.get(model.fromCompanyID)?.name}</p>
        <p>{companiesById.get(model.toCompanyID)?.name}</p>
      </span>
      <span>
        <p>Postnr</p>
        <p>{drivingTimes.fromAddress.zip}</p>
        <p>{drivingTimes.toAddress.zip}</p>
      </span>
      <span>
        <p>Ort</p>
        <p>
          {zipCodeLocalAreaFromZipCode(drivingTimes.fromAddress.zip)?.name ??
            drivingTimes.fromAddress.county?.areaName}
        </p>
        <p>
          {zipCodeLocalAreaFromZipCode(drivingTimes.toAddress.zip)?.name ??
            drivingTimes.toAddress.county?.areaName}
        </p>
      </span>
      <span>
        <p>Status</p>
        <p>{assignmentRequestStatuses[model.assignmentRequestStatusID]}</p>
      </span>
      <RowActionWrapper>
        <CaseButton
          onClick={() => {
            modalStack.push(
              <AssignmentRequestModalWrapper
                requestId={model.requestID}
                onClose={() => modalStack.pop()}
              />
            );
          }}
          unpadded
        >
          <FontAwesomeIcon
            style={{ marginLeft: 'auto' }}
            icon={faExternalLinkAlt}
          />
        </CaseButton>
      </RowActionWrapper>
    </RowWrapper>
  );
};

interface Props {
  availability: AvailabilityDto;
  user: UserModel;
  /**
   * Optional externally-owned booked-assignments mutation. When provided, the widget
   * skips its own fetch on mount and uses this instance — so a parent (e.g. the
   * availability modal) can share the result with sibling views like WorkDayMap.
   */
  externalBookedAssignmentsCall?: ReturnType<
    typeof useAssignmentGetBookedAssignmentsForUserAndDate
  >;
  /**
   * Companion to externalBookedAssignmentsCall — called whenever the widget would
   * otherwise refetch (e.g. after a child action updates assignments).
   */
  externalRefreshBookedAssignments?: () => Promise<void> | void;
}
const WorkTimeWidget: React.FC<Props> = ({
  availability,
  user,
  externalBookedAssignmentsCall,
  externalRefreshBookedAssignments,
}) => {
  const queryClient = useQueryClient();
  const [showTransferPicker, setShowTransferPicker] = React.useState(false);
  const [selectedTargetUserId, setSelectedTargetUserId] = React.useState<
    number | null
  >(null);
  const [selectedUserExtra, setSelectedUserExtra] =
    React.useState<ExtendedDayTransferUserModel | null>(null); // capture extra info
  const [showTransferConfirm, setShowTransferConfirm] = useState(false); // confirm modal visibility
  const [transferCount, setTransferCount] = useState(0); // number of items to transfer
  // new state to differentiate transfer vs unassign
  const [isUnassigning, setIsUnassigning] = useState(false);

  const { zipCodeAreaFromZipCode } = useZipCodeAreas();
  const { parsedData } = useGlobalSettings();
  const ftIsExcludedFromFulldayPlanning =
    !!parsedData[
      GlobalSettingEnum.UsersExcludedFromPlaceholderAssignmentRule
    ]?.[user.userID];

  const { assignmentTypes, stayOverTypes } = useTranslations();

  const { isOutsideAllowedRange } = useZipCodeAreas();
  const logisticsVicinityCheck = useIsInLogisticVicinity();

  const assignmentRequestsCall =
    useAssignmentRequestsGetAssignmentRequestsForUserAndDate({
      userId: user.userID,
      date: availability.date,
    });

  const ownBookedAssignmentsCall =
    useAssignmentGetBookedAssignmentsForUserAndDate();
  const bookedAssignmentsCall =
    externalBookedAssignmentsCall ?? ownBookedAssignmentsCall;

  const getBookedAssignments = async () => {
    if (externalRefreshBookedAssignments) {
      await externalRefreshBookedAssignments();
      return;
    }
    try {
      await ownBookedAssignmentsCall.mutateAsync({
        params: {
          userId: user.userID,
          date: availability.date,
        },
      });
    } catch (error) {
      toast.error('Kunde inte hämta bokade uppdrag');
    }
  };

  const refreshDay = async () => {
    await queryClient.invalidateQueries([assignmentRequestsCall.queryKey]);
    getBookedAssignments();
    assignmentRequestsCall.refetch();
  };

  const refreshingDay =
    bookedAssignmentsCall.isLoading || assignmentRequestsCall.isLoading;

  const [showSendPlannedConfirm, setShowSendPlannedConfirm] = useState(false);

  const {
    mutateAsync: sendPlannedAssignmentsForUser,
    isLoading: isSendingPlanned,
  } = useAssignmentSendPlannedAssignmentsForUser();

  const plannedAssignments = useMemo(
    () =>
      bookedAssignmentsCall.data?.assignments?.filter(
        (a) => a.assignment.assignmentStatusID === AssignmentStatusEnum.Planned
      ) ?? [],
    [bookedAssignmentsCall.data?.assignments]
  );

  const handleSendPlanned = async () => {
    try {
      const result = await sendPlannedAssignmentsForUser({
        data: {
          userId: user.userID,
          date: new Date(availability.date),
        },
      });
      toast.success(`${result.sentCount} uppdrag skickade till fälttestaren`);
      if (result.skippedAssignments && result.skippedAssignments.length > 0) {
        const lines = result.skippedAssignments
          .map(
            (s) =>
              `${s.registrationNumber ?? `Uppdrag #${s.assignmentId}`}: ${s.reason}`
          )
          .join('\n');
        toast(
          `${result.skippedAssignments.length} uppdrag hoppades över:\n${lines}`,
          {
            duration: 10000,
            icon: '\u26A0\uFE0F',
            style: { whiteSpace: 'pre-line' },
          }
        );
      }
      // Rastplaneraren (backend) lägger saknad rast innan utskicket och rapporterar vad den gjorde.
      // Fältet läses defensivt tills Orval-typerna regenererats med breakPlanningMessages.
      const breakPlanningMessages =
        (result as unknown as { breakPlanningMessages?: string[] })
          .breakPlanningMessages ??
        [];
      if (breakPlanningMessages.length > 0) {
        toast(`Rast:\n${breakPlanningMessages.join('\n')}`, {
          duration: 10000,
          icon: '\u23F8\uFE0F',
          style: { whiteSpace: 'pre-line' },
        });
      }
      refreshDay();
    } catch {
      toast.error('Kunde inte skicka planerade uppdrag');
    } finally {
      setShowSendPlannedConfirm(false);
    }
  };

  const { transferAssignments, isTransferring } = useTransferAssignments({
    onSuccess: () => {
      setShowTransferPicker(false);
      setSelectedTargetUserId(null);
      setSelectedUserExtra(null);
      setShowTransferConfirm(false);
      setIsUnassigning(false);
      refreshDay();
    },
  });

  const handleTransferClick = () => {
    setShowTransferPicker(!showTransferPicker);
  };

  const handleConfirmTransfer = async () => {
    if (!selectedTargetUserId) {
      toast.error('Välj en fälttestare att flytta uppdraget till');
      return;
    }

    const totalItems =
      (bookedAssignmentsCall.data?.assignments?.length || 0) +
      (assignmentRequestsCall.data?.length || 0);

    if (totalItems === 0) {
      toast.error('Inga uppdrag att flytta');
      return;
    }

    setTransferCount(totalItems);
    setIsUnassigning(false); // transfer flow
    setShowTransferConfirm(true);
  };

  const handleUnassign = async () => {
    const totalItems =
      (bookedAssignmentsCall.data?.assignments?.length || 0) +
      (assignmentRequestsCall.data?.length || 0);

    if (totalItems === 0) {
      toast.error('Inga uppdrag att flytta');
      return;
    }
    setTransferCount(totalItems);
    setIsUnassigning(true); // unassign flow
    setShowTransferConfirm(true);
  };

  // Fetch on mount only when we own the call ourselves. If the parent passed
  // an external instance, it's responsible for kicking off (and refreshing) it.
  useEffect(
    () => {
      if (!externalBookedAssignmentsCall) {
        getBookedAssignments();
      }
    } /* eslint-disable react-hooks/exhaustive-deps */,
    [availability.dateString, externalBookedAssignmentsCall != null]
  );

  const rawDayDrivingTimes = useDayDrivingTimes({
    bookedAssignments: bookedAssignmentsCall.data,
    assignmentRequests: assignmentRequestsCall.data,
    userId: user.userID,
  });

  // Externa lastbilstransportörer omfattas inte av heldagsplaneringen. Nollställ
  // planeringsflaggorna (utanför hemområde + rast) så att alla härledda varningar och
  // utfyllnadsförslag i widgeten försvinner för dem — utan att röra start-/sluttider eller
  // adresser. Backend slutar dessutom flagga dem (FieldTesterPlanningLogic), så inget blockerar.
  const isExternalTruckTransport = !!user.isTruckTransport;
  const dayDrivingTimes = useMemo(
    () =>
      isExternalTruckTransport
        ? rawDayDrivingTimes.map((entry) => ({
            ...entry,
            startsAwayFromPreviousLocation: false,
            endsAwayFromNextLocation: false,
            hasEnoughBreakTimeUntilAssignmentEnd: undefined,
            hasTooMuchBreakTimeUntilAssignmentEnd: undefined,
          }))
        : rawDayDrivingTimes,
    [rawDayDrivingTimes, isExternalTruckTransport]
  );

  const {
    handleCreateMorningPlaceholder,
    handleMidAssignmentPlaceholder,
    handleCreateAfternoonPlaceholder,
  } = useCreatePlaceholderAssignments({
    bookedAssignments: bookedAssignmentsCall.data?.assignments,
    drivingTimes: dayDrivingTimes,
    userId: user.userID,
    availability,
  });

  const {
    handleCreateMorningAssignmentRequest,
    handleCreateMiddayAssignmentRequest,
    handleCreateAfternoonAssignmentRequest,
  } = useCreateAssignmentRequests({
    bookedAssignments: bookedAssignmentsCall.data?.assignments,
    assignmentRequests: assignmentRequestsCall.data,
    dayDrivingTimes,
    userId: user.userID,
    availability,
  });

  const {
    handleFindSuitableAfternoonReturnAssignments,
    handleFindSuitableMiddayAssignments,
    handleFindSuitableMorningAssignments,
    loading,
  } = useFindSuitableAssignments({
    drivingTimes: dayDrivingTimes,
    userId: user.userID,
  });

  const firstAssignmentOfDay = dayDrivingTimes[0];
  const lastAssignmentOfDay = dayDrivingTimes[dayDrivingTimes.length - 1];

  const startToEndTimeStr = useMemo(() => {
    if (!firstAssignmentOfDay || !lastAssignmentOfDay) {
      return '';
    }
    return `${formatTime(firstAssignmentOfDay.start)} - ${formatTime(
      lastAssignmentOfDay.end
    )}`;
  }, [firstAssignmentOfDay, lastAssignmentOfDay]);

  const notEnoughBreakTime = useMemo(() => {
    const notEnough = dayDrivingTimes.find(
      (x) => x.hasEnoughBreakTimeUntilAssignmentEnd === false
    );
    return notEnough;
  }, [dayDrivingTimes]);

  const tooMuchBreakTime = useMemo(() => {
    return dayDrivingTimes.find(
      (x) => x.hasTooMuchBreakTimeUntilAssignmentEnd === true
    );
  }, [dayDrivingTimes]);

  const stayoverPreviousNight = bookedAssignmentsCall.data?.stayOverNightBefore;
  const hasStayoverPreviousNight =
    stayoverPreviousNight !== undefined && stayoverPreviousNight !== null;

  // Day-level "ofullständig dagsplanering" reasons surfaced from backend so we
  // can show them inline instead of the generic toast text.
  const { dayIncompleteReasons, contributingAssignmentIdsByReason } = useMemo(
    () =>
      extractIncompletePlanFields(
        bookedAssignmentsCall.data as unknown as IncompletePlanFieldsCarrier
      ),
    [bookedAssignmentsCall.data]
  );
  const reasonsByAssignmentId = useMemo(() => {
    const map = new Map<number, DayIncompleteReasonEnum[]>();
    (
      Object.entries(contributingAssignmentIdsByReason) as [
        string,
        number[],
      ][]
    ).forEach(([reasonKey, ids]) => {
      const reason = Number(reasonKey) as DayIncompleteReasonEnum;
      ids.forEach((id) => {
        const existing = map.get(id) ?? [];
        if (!existing.includes(reason)) existing.push(reason);
        map.set(id, existing);
      });
    });
    return map;
  }, [contributingAssignmentIdsByReason]);

  // Determine if selected user is unavailable (warning contains IsNotAvailable)
  const selectedUserIsUnavailable = selectedUserExtra?.warnings?.includes(
    AssigneeWarning.IsNotAvailable
  );
  // dynamic confirm modal content
  const confirmModalContent = isUnassigning
    ? `Vill du verkligen ta bort tilldelningen av ${transferCount} uppdrag/förfrågningar från ${user.name}?`
    : `Vill du verkligen flytta ${transferCount} uppdrag/förfrågningar till den valda fälttestaren?`;

  return (
    <Wrapper>
      <hr />
      {!isExternalTruckTransport && dayIncompleteReasons.length > 0 && (
        <Label
          level="error"
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2 }}
        >
          <strong>
            <FontAwesomeIcon icon={faWarning} /> Ofullständig dagsplanering
            – uppdragen kommer inte att skickas till fälttestaren
          </strong>
          {dayIncompleteReasons.map((r) => (
            <span key={r}>• {DAY_INCOMPLETE_REASON_LABELS[r] ?? r}</span>
          ))}
        </Label>
      )}
      <Title style={{ marginTop: 5, display: 'flex' }}>
        <span>Arbetsdag: {startToEndTimeStr}</span>
        {notEnoughBreakTime && (
          <Label
            level="error"
            style={{ marginLeft: 'auto', fontSize: 'medium' }}
          >
            <FontAwesomeIcon icon={faClock} /> Otillräcklig rast under
            arbetssdagen
          </Label>
        )}
        {!notEnoughBreakTime && tooMuchBreakTime && (
          <Label
            level="info"
            style={{
              marginLeft: 'auto',
              fontSize: 'medium',
              color: '#1976d2',
            }}
            title="Planerad rast överstiger kravet – inte ett regelbrott, bara en policy-avvikelse"
          >
            <FontAwesomeIcon icon={faClock} /> Mer rast än krävt under
            arbetsdagen
          </Label>
        )}
        <TextButton
          style={{
            marginLeft: notEnoughBreakTime || tooMuchBreakTime ? '10px' : 'auto',
            fontSize: 'small',
          }}
          onClick={handleTransferClick}
          disabled={
            refreshingDay || isTransferring || user.canDriveCarTransportTrucks
          }
          title="Flytta samtliga uppdrag till annan fälttestare"
        >
          <FontAwesomeIcon icon={faExchangeAlt} />
        </TextButton>
        <TextButton
          style={{
            marginLeft: '10px',
            fontSize: 'small',
          }}
          onClick={() => setShowSendPlannedConfirm(true)}
          disabled={
            refreshingDay || plannedAssignments.length === 0 || isSendingPlanned
          }
          title="Skicka planerade uppdrag till fälttestaren"
        >
          <FontAwesomeIcon
            icon={isSendingPlanned ? faSpinner : faPaperPlane}
            spin={isSendingPlanned}
          />
        </TextButton>
        <TextButton
          style={{
            marginLeft: '10px',
            fontSize: 'small',
          }}
          onClick={refreshDay}
          disabled={refreshingDay}
        >
          <FontAwesomeIcon
            icon={refreshingDay ? faSpinner : faRefresh}
            spin={refreshingDay}
          />
        </TextButton>
      </Title>
      {showTransferPicker && (
        <div
          style={{
            display: 'flex',
            gap: '10px',
            marginBottom: '10px',
            alignItems: 'center',
            flexDirection: 'column',
            alignSelf: 'stretch',
          }}
        >
          <div
            style={{
              display: 'flex',
              width: '100%',
              gap: '10px',
              alignItems: 'center',
            }}
          >
            <span
              style={{
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <FontAwesomeIcon
                icon={faFlask}
                style={{ color: 'red' }}
                title="Detta är en BETA-funktion"
              />
              Flytta dagens uppdrag till:
            </span>
            <div style={{ flex: 1, maxWidth: '400px' }}>
              <PrioritizedDayTransferUserPicker
                selectedUserId={selectedTargetUserId}
                fromUserId={user.userID}
                date={availability.dateString}
                onUserPicked={(userModel) =>
                  setSelectedTargetUserId(userModel?.userID || null)
                }
                onSelectionExtraChanged={(extra) => setSelectedUserExtra(extra)}
                autoFocus
              />
            </div>
            <TextButton
              onClick={handleConfirmTransfer}
              disabled={isTransferring || !selectedTargetUserId}
              style={{
                padding: '8px 16px',
                backgroundColor: '#4CAF50',
                color: 'white',
                borderRadius: '4px',
              }}
            >
              {isTransferring && !isUnassigning ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin /> Flyttar...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faExchangeAlt} /> Bekräfta flytt
                </>
              )}
            </TextButton>
            <TextButton
              onClick={() => {
                setShowTransferPicker(false);
                setSelectedTargetUserId(null);
                setSelectedUserExtra(null);
                setIsUnassigning(false);
              }}
              disabled={isTransferring}
              style={{ padding: '8px 16px' }}
            >
              Avbryt
            </TextButton>
          </div>
          <div
            style={{
              display: 'flex',
              width: '100%',
              justifyContent: 'flex-start',
            }}
          >
            <TextButton
              onClick={() => {
                handleUnassign();
              }}
              disabled={isTransferring}
              style={{
                padding: '8px 16px',
                backgroundColor: '#dd1818ff',
                color: 'white',
                borderRadius: '4px',
              }}
              title="Ta bort fälttestare från dagens alla uppdrag"
            >
              Ta bort FT från dagens alla uppdrag
            </TextButton>
          </div>
          {selectedUserIsUnavailable && selectedUserExtra?.user && (
            <Warn style={{ alignSelf: 'flex-start' }}>
              Tillgängligheten för {selectedUserExtra.user.name} kommer att
              uppdateras för att matcha uppdragen.
            </Warn>
          )}
        </div>
      )}
      <div style={{ marginBottom: 5, display: 'flex' }}>
        Hemort:{' '}
        {user.city ??
          user.zipCodeLocalArea?.name ??
          zipCodeAreaFromZipCode(user.postalCode)?.areaName ??
          'okänd'}
        {user.isAllowedHalfdayPlanning && (
          <span>
            {', '}
            halvdagsplanering tillåten
          </span>
        )}
      </div>
      {ftIsExcludedFromFulldayPlanning && (
        <Warn>
          Denna användare är exkluderad från planering för hela dagen, så du
          behöver inte skapa utfyllnadsuppdrag.
        </Warn>
      )}
      {refreshingDay ? (
        <LoadingSpinner>Laddar dag...</LoadingSpinner>
      ) : (
        <>
          <AddressWrapper>
            <span style={{ flex: 1 }}>
              <Address
                className={
                  firstAssignmentOfDay?.startsAwayFromPreviousLocation &&
                  !hasStayoverPreviousNight
                    ? firstAssignmentOfDay &&
                      isCanceledEntry(firstAssignmentOfDay)
                      ? 'warning'
                      : 'error'
                    : ''
                }
                title={`Startar ${firstAssignmentOfDay?.type ?? ''} ${firstAssignmentOfDay?.assignment?.case?.registrationNumber ? `(${firstAssignmentOfDay?.assignment?.case?.registrationNumber})` : ''}`}
                name={firstAssignmentOfDay?.fromAddress.name ?? '-'}
                address={firstAssignmentOfDay?.fromAddress.address ?? '-'}
                city={firstAssignmentOfDay?.fromAddress.city ?? '-'}
                zip={firstAssignmentOfDay?.fromAddress.zip ?? '-'}
                county={firstAssignmentOfDay?.fromAddress.county}
              >
                {hasStayoverPreviousNight && (
                  <Label level="warning">
                    <FontAwesomeIcon icon={faHotel} />{' '}
                    {stayOverTypes[stayoverPreviousNight]} föregående natt
                  </Label>
                )}
                {firstAssignmentOfDay?.startsAwayFromPreviousLocation &&
                firstAssignmentOfDay.stayoverType ? (
                  <Label>
                    <FontAwesomeIcon icon={faHotel} />{' '}
                    {stayOverTypes[firstAssignmentOfDay.stayoverType]}
                  </Label>
                ) : firstAssignmentOfDay?.startsAwayFromPreviousLocation &&
                  !hasStayoverPreviousNight ? (
                  <Label
                    level={
                      firstAssignmentOfDay &&
                      isCanceledEntry(firstAssignmentOfDay)
                        ? 'warning'
                        : 'error'
                    }
                  >
                    <FontAwesomeIcon icon={faWarning} /> Utanför hemområde
                  </Label>
                ) : null}
                {firstAssignmentOfDay?.assignment?.case.caseTypeID ===
                  CaseTypeEnum.Placeholder && (
                  <Label level="warning">
                    <FontAwesomeIcon
                      icon={getPlaceholderIcon(
                        firstAssignmentOfDay.assignment.assignment
                          .assignmentTypeID
                      )}
                    />{' '}
                    {
                      assignmentTypes[
                        firstAssignmentOfDay.assignment.assignment
                          .assignmentTypeID
                      ]
                    }
                  </Label>
                )}
              </Address>
            </span>
            <span style={{ flex: 1 }}>
              <Address
                className={
                  lastAssignmentOfDay?.endsAwayFromNextLocation &&
                  lastAssignmentOfDay?.stayoverType == null
                    ? lastAssignmentOfDay &&
                      isCanceledEntry(lastAssignmentOfDay)
                      ? 'warning'
                      : 'error'
                    : ''
                }
                title={`Slutar ${lastAssignmentOfDay?.type ?? ''} ${lastAssignmentOfDay?.assignment?.case?.registrationNumber ? `(${lastAssignmentOfDay?.assignment?.case?.registrationNumber})` : ''}`}
                name={lastAssignmentOfDay?.toAddress.name ?? '-'}
                address={lastAssignmentOfDay?.toAddress.address ?? '-'}
                city={lastAssignmentOfDay?.toAddress.city ?? '-'}
                zip={lastAssignmentOfDay?.toAddress.zip ?? '-'}
                county={lastAssignmentOfDay?.toAddress.county}
              >
                {lastAssignmentOfDay?.endsAwayFromNextLocation &&
                  lastAssignmentOfDay.stayoverType == null && (
                    <Label
                      level={
                        lastAssignmentOfDay &&
                        isCanceledEntry(lastAssignmentOfDay)
                          ? 'warning'
                          : 'error'
                      }
                    >
                      <FontAwesomeIcon icon={faWarning} /> Utanför hemområde
                    </Label>
                  )}
                {lastAssignmentOfDay?.stayoverType !== undefined &&
                  lastAssignmentOfDay?.stayoverType !== null && (
                    <Label level="warning">
                      <FontAwesomeIcon icon={faHotel} />{' '}
                      {stayOverTypes[lastAssignmentOfDay.stayoverType]}
                    </Label>
                  )}
                {lastAssignmentOfDay?.assignment?.case.caseTypeID ===
                  CaseTypeEnum.Placeholder && (
                  <Label level="warning">
                    <FontAwesomeIcon
                      icon={getPlaceholderIcon(
                        lastAssignmentOfDay.assignment.assignment
                          .assignmentTypeID
                      )}
                    />{' '}
                    {
                      assignmentTypes[
                        lastAssignmentOfDay.assignment.assignment
                          .assignmentTypeID
                      ]
                    }
                  </Label>
                )}
              </Address>
            </span>
          </AddressWrapper>
          {dayDrivingTimes.length > 0 &&
            dayDrivingTimes[0]?.startsAwayFromPreviousLocation &&
            !hasStayoverPreviousNight && (
              <Row key="blank-start">
                <DropdownMenu
                  label={
                    <span>
                      Lägg in utfyllnadsuppdrag{' '}
                      <FontAwesomeIcon icon={faPlus} />
                    </span>
                  }
                  content={
                    <>
                      <CaseButton
                        unpadded
                        style={{ border: '1px solid', padding: 5 }}
                        onClick={() => {
                          handleFindSuitableMorningAssignments();
                        }}
                        disabled={loading}
                      >
                        Hitta passande uppdrag
                        {loading ? (
                          <FontAwesomeIcon
                            style={{ marginLeft: 'auto' }}
                            icon={faSpinner}
                            spinPulse
                          />
                        ) : (
                          <FontAwesomeIcon
                            style={{ marginLeft: 'auto' }}
                            icon={faCar}
                          />
                        )}
                      </CaseButton>
                      <>
                        <CaseButton
                          unpadded
                          style={{ border: '1px solid', padding: 5 }}
                          onClick={() => {
                            handleCreateMorningPlaceholder(
                              PlaceholderAssignmentTypeEnum.CarPlaceholder
                            );
                          }}
                        >
                          Blank körning
                          <span>
                            <FontAwesomeIcon
                              style={{ marginLeft: 'auto' }}
                              icon={faCar}
                            />
                            <FontAwesomeIcon
                              style={{ marginLeft: 'auto' }}
                              icon={faQuestionCircle}
                              size="xs"
                            />
                          </span>
                        </CaseButton>
                        <CaseButton
                          unpadded
                          style={{ border: '1px solid', padding: 5 }}
                          onClick={() => {
                            handleCreateMorningPlaceholder(
                              PlaceholderAssignmentTypeEnum.PersonalTransport
                            );
                          }}
                        >
                          Kollektivtrafik
                          <FontAwesomeIcon
                            style={{ marginLeft: 'auto' }}
                            icon={faBus}
                          />
                        </CaseButton>
                      </>
                      <CaseButton
                        unpadded
                        style={{ border: '1px solid', padding: 5 }}
                        onClick={() => {
                          handleCreateMorningAssignmentRequest();
                        }}
                      >
                        Begär annonsbil
                        <FontAwesomeIcon
                          style={{ marginLeft: 'auto' }}
                          icon={faHand}
                        />
                      </CaseButton>
                    </>
                  }
                />
                <Label
                  level={
                    dayDrivingTimes[0] && isCanceledEntry(dayDrivingTimes[0])
                      ? 'warning'
                      : 'error'
                  }
                >
                  <FontAwesomeIcon icon={faWarning} /> Startar utanför hemområde
                </Label>
              </Row>
            )}
          <>
            {dayDrivingTimes.map((entry, index) => {
              const key =
                entry.assignment?.assignment.assignmentID ??
                entry.assignmentRequest?.requestID ??
                index;

              // Externa lastbilstransportörer omfattas inte av heldagsplaneringen — visa inga
              // avstånds-/tidsglappsvarningar (eller utfyllnadsförslag) mellan deras uppdrag.
              const startsFarFromPreviousAssignmentEnd =
                !isExternalTruckTransport &&
                index > 0 &&
                isOutsideAllowedRange(
                  entry.fromAddress.zip,
                  dayDrivingTimes[index - 1]?.toAddress?.zip
                );

              const timeBetweenAssignments = entry.timeFromPreviousEnd ?? 0;
              const tooShortTimeBetweenAssignments =
                timeBetweenAssignments < MIN_TIME_BETWEEN_ASSIGNMENTS_MILLIS;

              const previousAssignmentEndsInVicinityofLogistics =
                logisticsVicinityCheck(
                  dayDrivingTimes[index - 1]?.toAddress?.zip
                );

              const timeGapBetweenAssignments =
                !isExternalTruckTransport &&
                (entry.timeFromPreviousEnd ?? 0) >
                  MIN_TIME_BETWEEN_ASSIGNMENTS_MILLIS;

              // If either the current or previous assignment is cancelled,
              // show location gap warnings as orange (warning) instead of red (error)
              const gapInvolvesCancelledAssignment =
                isCanceledEntry(entry) ||
                (index > 0 && isCanceledEntry(dayDrivingTimes[index - 1]));

              return (
                <React.Fragment key={key}>
                  {startsFarFromPreviousAssignmentEnd && (
                    <Row>
                      <DropdownMenu
                        label={
                          <span>
                            Lägg in utfyllnadsuppdrag{' '}
                            <FontAwesomeIcon icon={faPlus} />
                          </span>
                        }
                        content={
                          <>
                            <CaseButton
                              unpadded
                              style={{ border: '1px solid', padding: 5 }}
                              onClick={() => {
                                handleFindSuitableMiddayAssignments(
                                  index - 1,
                                  index
                                );
                              }}
                              disabled={loading}
                            >
                              Hitta passande uppdrag
                              {loading ? (
                                <FontAwesomeIcon
                                  style={{ marginLeft: 'auto' }}
                                  icon={faSpinner}
                                  spinPulse
                                />
                              ) : (
                                <FontAwesomeIcon
                                  style={{ marginLeft: 'auto' }}
                                  icon={faCar}
                                />
                              )}
                            </CaseButton>
                            <>
                              <CaseButton
                                unpadded
                                style={{ border: '1px solid', padding: 5 }}
                                onClick={() =>
                                  handleMidAssignmentPlaceholder(
                                    dayDrivingTimes[index - 1],
                                    entry,
                                    PlaceholderAssignmentTypeEnum.CarPlaceholder
                                  )
                                }
                              >
                                Blank körning
                                <span>
                                  <FontAwesomeIcon
                                    style={{ marginLeft: 'auto' }}
                                    icon={faCar}
                                  />
                                  <FontAwesomeIcon
                                    style={{ marginLeft: 'auto' }}
                                    icon={faQuestionCircle}
                                    size="xs"
                                  />
                                </span>
                              </CaseButton>
                              <CaseButton
                                unpadded
                                style={{ border: '1px solid', padding: 5 }}
                                onClick={() =>
                                  handleMidAssignmentPlaceholder(
                                    dayDrivingTimes[index - 1],
                                    entry,
                                    PlaceholderAssignmentTypeEnum.PersonalTransport
                                  )
                                }
                              >
                                Kollektivtrafik
                                <FontAwesomeIcon
                                  style={{ marginLeft: 'auto' }}
                                  icon={faBus}
                                />
                              </CaseButton>
                            </>
                            {previousAssignmentEndsInVicinityofLogistics ===
                              true && (
                              <CaseButton
                                unpadded
                                style={{ border: '1px solid', padding: 5 }}
                                onClick={() => {
                                  handleCreateMiddayAssignmentRequest(index);
                                }}
                              >
                                Begär annonsbil
                                <FontAwesomeIcon
                                  style={{ marginLeft: 'auto' }}
                                  icon={faHand}
                                />
                              </CaseButton>
                            )}
                          </>
                        }
                      />

                      <Label
                        level={
                          gapInvolvesCancelledAssignment ? 'warning' : 'error'
                        }
                      >
                        <p>
                          <FontAwesomeIcon icon={faWarning} /> Uppdraget slutar
                          långt ifrån nästa uppdrag
                        </p>
                        {tooShortTimeBetweenAssignments && (
                          <p>
                            <FontAwesomeIcon icon={faWarning} /> Kort tid mellan
                            uppdragen för avståndet (
                            {getHoursAndMinutesFromMillisecondsString(
                              timeBetweenAssignments
                            )}
                            )
                          </p>
                        )}
                      </Label>
                    </Row>
                  )}
                  {timeGapBetweenAssignments && (
                    <Row>
                      <Label level="warning">
                        <FontAwesomeIcon icon={faClock} /> Det är ett tidsglapp
                        på {(entry.timeFromPreviousEnd ?? 0) / 60000} minuter
                        mellan uppdragen
                      </Label>
                    </Row>
                  )}
                  {entry.assignment && (
                    <AssignmentRow
                      model={entry.assignment}
                      drivingTimes={entry}
                      onUpdate={refreshDay}
                      incompleteReasons={reasonsByAssignmentId.get(
                        entry.assignment.assignment.assignmentID
                      )}
                    />
                  )}
                  {entry.assignmentRequest && (
                    <AssignmentRequestRow
                      model={entry.assignmentRequest}
                      drivingTimes={entry}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </>

          {dayDrivingTimes.length > 0 &&
            // dayDrivingTimes[dayDrivingTimes.length - 1]?.assignment?.case
            //   ?.caseTypeID !== CaseTypeEnum.Placeholder &&
            dayDrivingTimes[dayDrivingTimes.length - 1]
              ?.endsAwayFromNextLocation &&
            // == null: uppdrag utan övernattning har stayoverType null, medan
            // annonsförfrågningar aldrig får fältet satt (undefined).
            dayDrivingTimes[dayDrivingTimes.length - 1]?.stayoverType ==
              null && (
              <Row key="blank-end">
                <DropdownMenu
                  label={
                    <span>
                      Lägg in utfyllnadsuppdrag{' '}
                      <FontAwesomeIcon icon={faPlus} />
                    </span>
                  }
                  content={
                    <>
                      <CaseButton
                        unpadded
                        style={{ border: '1px solid', padding: 5 }}
                        onClick={() => {
                          handleFindSuitableAfternoonReturnAssignments();
                        }}
                        disabled={loading}
                      >
                        Hitta passande uppdrag
                        {loading ? (
                          <FontAwesomeIcon
                            style={{ marginLeft: 'auto' }}
                            icon={faSpinner}
                            spinPulse
                          />
                        ) : (
                          <FontAwesomeIcon
                            style={{ marginLeft: 'auto' }}
                            icon={faCar}
                          />
                        )}
                      </CaseButton>
                      <>
                        <CaseButton
                          unpadded
                          style={{ border: '1px solid', padding: 5 }}
                          onClick={() =>
                            handleCreateAfternoonPlaceholder(
                              PlaceholderAssignmentTypeEnum.CarPlaceholder
                            )
                          }
                        >
                          Blank körning
                          <span>
                            <FontAwesomeIcon
                              style={{ marginLeft: 'auto' }}
                              icon={faCar}
                            />
                            <FontAwesomeIcon
                              style={{ marginLeft: 'auto' }}
                              icon={faQuestionCircle}
                              size="xs"
                            />
                          </span>
                        </CaseButton>
                        <CaseButton
                          unpadded
                          style={{ border: '1px solid', padding: 5 }}
                          onClick={() =>
                            handleCreateAfternoonPlaceholder(
                              PlaceholderAssignmentTypeEnum.PersonalTransport
                            )
                          }
                        >
                          Kollektivtrafik
                          <FontAwesomeIcon
                            style={{ marginLeft: 'auto' }}
                            icon={faBus}
                          />
                        </CaseButton>
                      </>
                      {logisticsVicinityCheck(
                        dayDrivingTimes[dayDrivingTimes.length - 1].toAddress
                          .zip
                      ) && (
                        <CaseButton
                          unpadded
                          style={{ border: '1px solid', padding: 5 }}
                          onClick={() => {
                            handleCreateAfternoonAssignmentRequest();
                          }}
                        >
                          Begär annonsbil
                          <FontAwesomeIcon
                            style={{ marginLeft: 'auto' }}
                            icon={faHand}
                          />
                        </CaseButton>
                      )}
                    </>
                  }
                />
                <Label
                  level={
                    dayDrivingTimes[dayDrivingTimes.length - 1] &&
                    isCanceledEntry(dayDrivingTimes[dayDrivingTimes.length - 1])
                      ? 'warning'
                      : 'error'
                  }
                >
                  <FontAwesomeIcon icon={faWarning} /> Slutar utanför hemområde
                </Label>
              </Row>
            )}
          {!bookedAssignmentsCall.data?.assignments.length && (
            <Row>Inga bokade uppdrag</Row>
          )}
        </>
      )}
      {showSendPlannedConfirm && (
        <ConfirmModal
          title="Skicka planerade uppdrag"
          content={`Vill du skicka ${plannedAssignments.length} planerade uppdrag till ${user.name}?`}
          disabled={isSendingPlanned}
          confirmText={isSendingPlanned ? 'Skickar...' : 'Ja'}
          cancelText="Nej"
          onConfirm={handleSendPlanned}
          onCancel={() => setShowSendPlannedConfirm(false)}
        />
      )}
      {showTransferConfirm && (
        <ConfirmModal
          title={
            isUnassigning ? 'Ta bort uppdragstilldelning' : 'Flytta uppdrag'
          }
          content={confirmModalContent}
          disabled={isTransferring}
          confirmText={
            isTransferring
              ? isUnassigning
                ? 'Tar bort...'
                : 'Flyttar...'
              : 'Ja'
          }
          cancelText="Nej"
          onConfirm={async () => {
            await transferAssignments(
              user.userID,
              isUnassigning ? 0 : selectedTargetUserId!,
              new Date(availability.date)
            );
          }}
          onCancel={() => {
            setShowTransferConfirm(false);
            setIsUnassigning(false);
          }}
        />
      )}
    </Wrapper>
  );
};

export default WorkTimeWidget;
